import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Autocomplete, TextField, Box, useTheme } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { DistributionManager } from '../../../APIManager'
import { type IDistributionSeries } from '../../../models/DistributionSeries'
import GenericButton from '../../../components/GenericButton'
import { ButtonSize, ButtonType } from '../../../components/GenericButton/styles'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { useAppSearchParams } from '../../../hooks/useAppSearchParams'
import { useAppLocalStorage } from '../../../hooks/useAppLocalStorage'
import { useSandboxReportsView, sandboxReportsRequestParam } from '../../../hooks/useSandboxReportsView'

interface ITerm {
  termId: string
  termName: string
}

interface ProjectAndTermHeaderProps {
  onApply?: (project: IDistributionSeries | null, term: ITerm | null) => void
  disabledProjectAndTermHeader?: boolean
  filterTermId?: string
}

const LOCAL_STORAGE_PROJECT_KEY = 'reportsSelectedProjectId'
const LOCAL_STORAGE_TERM_KEY = 'reportsSelectedTermId'

interface BuildProjectTermUrlParamsArgs {
  nextProjectId?: string
  nextTermId?: string
  urlProjectId: string | null
  urlTermId: string | null
}

export const buildProjectTermUrlParams = ({
  nextProjectId,
  nextTermId,
  urlProjectId,
  urlTermId
}: BuildProjectTermUrlParamsArgs): Record<string, string | null | undefined> => {
  const selectionChanged = nextProjectId !== urlProjectId || nextTermId !== urlTermId
  return {
    projectId: nextProjectId,
    termId: nextTermId,
    ...(selectionChanged ? { slotStatus: null } : {})
  }
}

export const ProjectAndTermHeader: React.FC<ProjectAndTermHeaderProps> = ({ onApply, disabledProjectAndTermHeader, filterTermId }) => {
  const { searchParams, setUrlParams } = useAppSearchParams()
  const { setLocalStorage } = useAppLocalStorage()
  // Set from the Sandbox Reports row in the reports ⋯ menu; this list is what it acts on.
  const { showSandboxOnly } = useSandboxReportsView()
  const sandboxReportsParam = sandboxReportsRequestParam(showSandboxOnly)
  const [selectedProject, setSelectedProject] = useState<IDistributionSeries | null>(null)
  const [selectedTerm, setSelectedTerm] = useState<ITerm | null>(null)
  const theme = useTheme()

  const autocompleteSx = useMemo(() => ({
    [theme.breakpoints.up('md')]: { maxWidth: '256px' }
  }), [theme])

  const urlProjectId = searchParams.get('projectId')
  const urlTermId = searchParams.get('termId')

  const activeFilterTermId = filterTermId ?? undefined

  const {
    data: reportsProjectsResponse,
    isLoading: projectsLoading
  } = useQuery({
    queryKey: ['ReportsProjects', activeFilterTermId, showSandboxOnly],
    queryFn: async () => await DistributionManager.getAllDistributionSeries({
      activeTerms: true,
      ...(activeFilterTermId != null ? { term_id: activeFilterTermId } : {}),
      ...sandboxReportsParam
    }),
    retry: false,
    enabled: true
  })

  // In instructor mode, the term-filtered query returns projects with a truncated terms
  // array (only the matched term). Fetch without term filter to get full terms for each
  // project so the term dropdown shows all available terms.
  const { data: unfilteredProjectsResponse } = useQuery({
    queryKey: ['ReportsProjects', showSandboxOnly],
    queryFn: async () => await DistributionManager.getAllDistributionSeries({ activeTerms: true, ...sandboxReportsParam }),
    retry: false,
    enabled: filterTermId != null
  })

  const reportsProjects = reportsProjectsResponse?.data || []

  const termOptions = useMemo(() => {
    if (filterTermId != null && selectedProject != null && unfilteredProjectsResponse?.data) {
      const fullProject = unfilteredProjectsResponse.data.find(
        p => p.data.distributionSeriesId === selectedProject.distributionSeriesId
      )
      if (fullProject?.data.terms) return fullProject.data.terms
    }
    return selectedProject?.terms ?? []
  }, [filterTermId, selectedProject, unfilteredProjectsResponse])

  const defaultProject = useMemo(() => {
    if (urlProjectId && reportsProjects.length) {
      const found = reportsProjects.find((p) => p.data.distributionSeriesId === urlProjectId)?.data
      if (found) return found
      // In instructor mode the URL project may be stale/deleted — fall back to first valid project
      if (filterTermId != null) return reportsProjects[0]?.data ?? null
    }
    const storedId = localStorage.getItem(LOCAL_STORAGE_PROJECT_KEY)
    if (storedId && reportsProjects.some((p) => p.data.distributionSeriesId === storedId)) {
      return reportsProjects.find((p) => p.data.distributionSeriesId === storedId)?.data || null
    }
    return null
  }, [urlProjectId, reportsProjects, filterTermId])

  const defaultTerm = useMemo(() => {
    if (urlTermId) {
      const termInProject = defaultProject?.terms?.find((t: ITerm) => t.termId === urlTermId)
      if (termInProject) return termInProject
      // In instructor mode, search across projects for the term object only when the
      // project IS found but its terms array lacks the filtered term (API behaviour when
      // term_id filter is used). Do not search when defaultProject is null — that means
      // the URL project is invalid for this term and both dropdowns should stay empty.
      if (filterTermId != null && defaultProject != null) {
        for (const { data: project } of reportsProjects) {
          const found = project.terms?.find((t: ITerm) => t.termId === urlTermId)
          if (found) return found
        }
        // Also search unfiltered projects for the term object
        if (unfilteredProjectsResponse?.data) {
          for (const { data: project } of unfilteredProjectsResponse.data) {
            const found = project.terms?.find((t: ITerm) => t.termId === urlTermId)
            if (found) return found
          }
        }
      }
    }
    const storedTermId = localStorage.getItem(LOCAL_STORAGE_TERM_KEY)
    if (storedTermId && defaultProject?.terms?.some((t: ITerm) => t.termId === storedTermId)) {
      return defaultProject.terms.find((t: ITerm) => t.termId === storedTermId) || null
    }
    return null
  }, [urlTermId, defaultProject, filterTermId, reportsProjects, unfilteredProjectsResponse])

  useEffect(() => {
    setSelectedProject(defaultProject)
    setSelectedTerm(defaultTerm)
  }, [defaultProject, defaultTerm])

  useEffect(() => {
    if (!urlProjectId && !urlTermId) {
      const storedProjectId = localStorage.getItem(LOCAL_STORAGE_PROJECT_KEY)
      const storedTermId = localStorage.getItem(LOCAL_STORAGE_TERM_KEY)
      if (storedProjectId && storedTermId) {
        setUrlParams({
          projectId: storedProjectId || undefined,
          termId: storedTermId || undefined
        })
      }
    }
  }, [])

  const handleProjectChange = useCallback((_: any, newValue: IDistributionSeries | null) => {
    setSelectedProject(newValue)
    if (filterTermId != null && newValue?.terms) {
      setSelectedTerm(newValue.terms.find((t: ITerm) => t.termId === filterTermId) ?? null)
    } else {
      setSelectedTerm(null)
    }
  }, [filterTermId])

  const handleTermChange = useCallback((_: any, newValue: ITerm | null) => {
    setSelectedTerm(newValue)
  }, [])

  const handleApply = useCallback(() => {
    const nextProjectId = selectedProject?.distributionSeriesId
    const nextTermId = selectedTerm?.termId

    setUrlParams(buildProjectTermUrlParams({ nextProjectId, nextTermId, urlProjectId, urlTermId }))
    setLocalStorage({
      [LOCAL_STORAGE_PROJECT_KEY]: nextProjectId,
      [LOCAL_STORAGE_TERM_KEY]: nextTermId
    })
    if (onApply) onApply(selectedProject, selectedTerm)
  }, [onApply, selectedProject, selectedTerm, setUrlParams, setLocalStorage, urlProjectId, urlTermId])

  const autocompleteProps = {
    autoHighlight: true,
    disableClearable: true,
    size: 'small' as const
  }

  return (
    <Box display="flex" alignItems="center" gap={2}>
      <Autocomplete
        options={reportsProjects.map(p => p.data)}
        value={selectedProject}
        onChange={handleProjectChange}
        getOptionLabel={(option: IDistributionSeries) => option.title}
        isOptionEqualToValue={(option: IDistributionSeries, value: IDistributionSeries) => option.distributionSeriesId === value.distributionSeriesId}
        loading={projectsLoading}
        disabled={disabledProjectAndTermHeader}
        {...autocompleteProps}
        renderInput={(params) => (
          <TextField {...params} placeholder="Select Project" variant="outlined" />
        )}
        sx={{
          ...autocompleteSx
        }}
        fullWidth
      />
      <Autocomplete
        options={termOptions}
        value={selectedTerm}
        onChange={handleTermChange}
        getOptionLabel={(option: ITerm) => option.termName}
        isOptionEqualToValue={(option: ITerm, value: ITerm) => option.termId === value.termId}
        loading={projectsLoading}
        disabled={disabledProjectAndTermHeader}
        {...autocompleteProps}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Select Term"
            variant="outlined"
          />
        )}
        sx={{ ...autocompleteSx }}
        fullWidth
      />
      <GenericButton
        buttonType={ButtonType.Primary}
        buttonSize={ButtonSize.Medium}
        icon={<ArrowForwardIcon />}
        onClick={handleApply}
      />
    </Box>
  )
}
